import { Component, ElementRef, Input, Renderer2, OnInit, OnDestroy, HostListener } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { DownloadService } from "src/app/shared/services/download.service";
import { LoadingService } from "src/app/shared/services/loading.service";
import { DownloadProgress } from "../download-progress/download-progress.component";
import {
  getBrowserPhotoDisplayUrl,
  heicPreviewUnavailableLabel,
  shouldRenderPhotoInBrowser,
} from "src/app/shared/utils/heic-display.util";

@Component({
  selector: "app-preview-popup",
  templateUrl: "./preview-popup.component.html",
  styleUrls: ["./preview-popup.component.scss"],
})
export class PreviewPopupComponent implements OnInit, OnDestroy {
  @Input() file: any;
  @Input() type: "file" | "photo" | "video" | "media" = "file";
  @Input() allowDownload: boolean = true;
  @Input() brand: any;
  @Input() files: any[] = []; // List of all files for navigation
  @Input() currentIndex: number = 0; // Current file index in the list

  zoomLevel = 1;
  isDragging = false;
  dragStartX = 0;
  dragStartY = 0;
  translateX = 0;
  translateY = 0;
  isDownloading = false;
  isImageLoading = false; // Loading state for image

  // Download progress properties
  downloadProgress: DownloadProgress = {
    current: 0,
    total: 0,
    percentage: 0,
    currentFile: "",
    status: "idle",
    message: "",
  };
  showDownloadProgress = false;
  downloadCancelled = false;

  // AbortController for cancelling downloads
  private downloadAbortController: AbortController | null = null;

  // Image preloading and caching
  private imageCache: Map<string, HTMLImageElement> = new Map();
  private preloadQueue: Set<number> = new Set();
  private navigationTimeout: any = null;
  private cacheCleanupInterval: any = null;
  private readonly PRELOAD_RANGE = 2; // Preload 2 images ahead and behind
  private readonly MAX_CACHE_SIZE = 50; // Maximum number of images to keep in cache

  constructor(
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    private loadingService: LoadingService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private downloadService: DownloadService
  ) {
    // Force apply brand styles when modal is created
    setTimeout(() => {
      this.applyBrandStyles();
    }, 0);
  }

  ngOnInit(): void {
    // Apply styles immediately
    this.applyBrandStyles();

    // Also apply after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.applyBrandStyles();
    }, 100);

    // Force apply after modal is fully rendered
    setTimeout(() => {
      this.applyBrandStyles();
    }, 500);

    // Additional force apply after longer delay to ensure all styles are applied
    setTimeout(() => {
      this.applyBrandStyles();
      this.forceStyleRefresh();
    }, 1000);

    // Preload adjacent images
    if ((this.type === "photo" || this.type === "media") && this.files.length > 0) {
      this.preloadAdjacentImages();

      // Set up periodic cache cleanup to prevent memory issues
      this.cacheCleanupInterval = setInterval(() => {
        this.clearImageCache();
      }, 30000); // Clean cache every 30 seconds
    }
  }

  private applyBrandStyles(): void {
    const hostElement = this.elementRef.nativeElement;

    // Test with a specific color if no brand is provided
    if (!this.brand) {
      this.renderer.setStyle(hostElement, "--brand-color", "#FF0000");
      this.renderer.setStyle(hostElement, "--brand-color-rgb", "255, 0, 0");
      this.renderer.setStyle(hostElement, "--brand-font", "'Arial', sans-serif");
      this.renderer.setStyle(hostElement, "--brand-font-computed", "'Arial', system-ui, sans-serif");
      return;
    }

    // Apply brand color
    if (this.brand.brandColor) {
      // Force set with !important equivalent
      this.renderer.setStyle(hostElement, "--brand-color", this.brand.brandColor, 2); // Priority 2 for important

      // Convert hex to RGB for rgba usage
      const rgb = this.hexToRgb(this.brand.brandColor);
      if (rgb) {
        this.renderer.setStyle(hostElement, "--brand-color-rgb", `${rgb.r}, ${rgb.g}, ${rgb.b}`, 2);
      }

      // Also set directly on document root for global override
      document.documentElement.style.setProperty("--brand-color", this.brand.brandColor, "important");
      if (rgb) {
        document.documentElement.style.setProperty("--brand-color-rgb", `${rgb.r}, ${rgb.g}, ${rgb.b}`, "important");
      }

      // Force override by setting inline styles on specific elements
      const elementsToStyle = hostElement.querySelectorAll(
        ".close-btn, .zoom-btn, .reset-btn, .__download-btn, .__close-btn, .download-btn"
      );
      elementsToStyle.forEach((element: HTMLElement) => {
        this.renderer.setStyle(element, "color", this.brand.brandColor, 2);
        this.renderer.setStyle(element, "border-color", this.brand.brandColor, 2);
      });
    }

    // Apply brand font
    if (this.brand.brandFont) {
      let fontValue = this.brand.brandFont;

      // Handle font mapping like in file-detail component
      const fontMapping: { [key: string]: string } = {
        Arial: "var(--font-arial)",
        Helvetica: "var(--font-helvetica)",
        "Times New Roman": "var(--font-times)",
        Georgia: "var(--font-georgia)",
        Verdana: "var(--font-verdana)",
        "Courier New": "var(--font-courier)",
        "Lucida Console": "var(--font-lucida)",
        "DejaVu Sans": "var(--font-dejavu-sans)",
        "DejaVu Serif": "var(--font-dejavu-serif)",
        Courier: "var(--font-courier-old)",
        Austin: "var(--font-austin)",
        "Atlas Grotesk": "var(--font-atlas-grotesk)",
        "Atlas Typewriter": "var(--font-atlas-typewriter)",
        "Lyon Text": "var(--font-lyon-text)",
        "Hurtiguten Display": "var(--font-hurtiguten-display)",
      };

      // Check if it's a mapped font name
      if (fontMapping[fontValue]) {
        fontValue = fontMapping[fontValue];
      } else if (!fontValue.includes("'") && !fontValue.includes('"')) {
        // If it's not already quoted and not in mapping, add quotes
        fontValue = `'${fontValue}', sans-serif`;
      }

      // Force set with !important equivalent
      this.renderer.setStyle(hostElement, "--brand-font", fontValue, 2);
      this.renderer.setStyle(hostElement, "--brand-font-computed", `${fontValue}, system-ui, sans-serif`, 2);

      // Also set directly on document root for global override
      document.documentElement.style.setProperty("--brand-font", fontValue, "important");
      document.documentElement.style.setProperty(
        "--brand-font-computed",
        `${fontValue}, system-ui, sans-serif`,
        "important"
      );

      // Force override by setting inline styles on text elements
      const textElements = hostElement.querySelectorAll("h3, p, .file-info, .zoom-level");
      textElements.forEach((element: HTMLElement) => {
        this.renderer.setStyle(element, "font-family", fontValue, 2);
      });
    }
  }

  private forceStyleRefresh(): void {
    const hostElement = this.elementRef.nativeElement;

    if (this.brand.brandColor) {
      // Force refresh by temporarily removing and re-adding the style
      const elementsToStyle = hostElement.querySelectorAll(
        ".close-btn, .zoom-btn, .reset-btn, .__download-btn, .__close-btn, .download-btn, .preview-actions__download-btn, .preview-actions__close-btn"
      );
      elementsToStyle.forEach((element: HTMLElement) => {
        // Remove existing styles
        element.style.removeProperty("color");
        element.style.removeProperty("border-color");
        element.style.removeProperty("background-color");

        // Re-apply with brand color
        this.renderer.setStyle(element, "color", this.brand.brandColor, 2);
        this.renderer.setStyle(element, "border-color", this.brand.brandColor, 2);

        // For download button, set background and ensure white text/icon
        if (
          element.classList.contains("__download-btn") ||
          element.classList.contains("preview-actions__download-btn")
        ) {
          this.renderer.setStyle(element, "background-color", this.brand.brandColor, 2);
          this.renderer.setStyle(element, "color", "white", 2);

          // Also ensure icon is white
          const icon = element.querySelector("i");
          if (icon) {
            this.renderer.setStyle(icon, "color", "white", 2);
          }
        }
      });
    }

    if (this.brand.brandFont) {
      const textElements = hostElement.querySelectorAll(
        "h3, p, .file-info, .zoom-level, .close-btn, .zoom-btn, .reset-btn, .__download-btn, .__close-btn"
      );
      textElements.forEach((element: HTMLElement) => {
        // Remove existing font-family
        element.style.removeProperty("font-family");

        // Re-apply with brand font
        let fontValue = this.brand.brandFont;
        if (!fontValue.includes("'") && !fontValue.includes('"')) {
          fontValue = `'${fontValue}', sans-serif`;
        }
        this.renderer.setStyle(element, "font-family", fontValue, 2);
      });
    }
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  }

  private getMediaType(file: any): string {
    return (file.type || "").toString().toLowerCase().trim();
  }

  private getFileNameForDetect(file: any): string {
    return (file.fileName || "").toString().toLowerCase().trim();
  }

  private getUrlPathForDetect(file: any): string {
    const rawUrl = (file.url || "").toString().toLowerCase().trim();
    if (!rawUrl) return "";
    return rawUrl.split("?")[0].split("#")[0];
  }

  private isImageFile(file: any): boolean {
    if (!file) return false;
    if (this.type === "photo") return true;
    if (this.type !== "media") return false;
    const mediaType = this.getMediaType(file);
    if (mediaType.startsWith("image") || mediaType === "photo") return true;
    const fileName = this.getFileNameForDetect(file);
    const urlPath = this.getUrlPathForDetect(file);
    return /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif)$/.test(fileName || urlPath);
  }

  private isVideoFile(file: any): boolean {
    if (!file) return false;
    if (this.type === "video") return true;
    if (this.type !== "media") return false;
    const mediaType = this.getMediaType(file);
    if (mediaType.startsWith("video")) return true;
    const fileName = this.getFileNameForDetect(file);
    const urlPath = this.getUrlPathForDetect(file);
    return /\.(mp4|webm|ogg|mov|avi|wmv|mkv|m4v)$/.test(fileName || urlPath);
  }

  isCurrentImage(): boolean {
    return this.isImageFile(this.file);
  }

  isCurrentVideo(): boolean {
    return this.isVideoFile(this.file);
  }

  getPhotoDisplayUrl(file: any = this.file): string {
    return getBrowserPhotoDisplayUrl(file);
  }

  shouldRenderPhotoPreview(file: any = this.file): boolean {
    return shouldRenderPhotoInBrowser(file);
  }

  getPhotoPreviewUnavailableLabel(file: any = this.file): string {
    return heicPreviewUnavailableLabel(file);
  }

  updateTransform(): void {
    const photoWrapper = this.elementRef.nativeElement.querySelector(".photo-wrapper");
    if (photoWrapper) {
      const transform = `scale(${this.zoomLevel}) translate(${this.translateX}px, ${this.translateY}px)`;
      this.renderer.setStyle(photoWrapper, "transform", transform);
    }
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + 0.25, 3);
    this.updateTransform();
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - 0.25, 0.5);
    this.updateTransform();
  }

  resetZoom(): void {
    this.zoomLevel = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.updateTransform();
  }

  onMouseDown(event: MouseEvent): void {
    if (this.isCurrentImage() && this.zoomLevel > 1) {
      this.isDragging = true;
      this.dragStartX = event.clientX - this.translateX;
      this.dragStartY = event.clientY - this.translateY;
      event.preventDefault();
    }
  }

  onMouseMove(event: MouseEvent): void {
    if (this.isDragging && this.isCurrentImage()) {
      this.translateX = event.clientX - this.dragStartX;
      this.translateY = event.clientY - this.dragStartY;
      this.updateTransform();
    }
  }

  onMouseUp(): void {
    this.isDragging = false;
  }

  onWheel(event: WheelEvent): void {
    if (this.isCurrentImage()) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }
  }

  close(): void {
    this.activeModal.close();
  }

  async downloadFile(): Promise<void> {
    if (!this.file.url) {
      this.toastr.error("Download URL not available", "Error");
      return;
    }

    const filename = this.file.fileName || this.file.url.split("/").pop() || "download";
    this.updateSingleFileProgress("preparing", `Preparing ${filename}...`);

    // Initialize download progress
    this.downloadAbortController = new AbortController();
    this.initializeDownloadProgress();
    this.isDownloading = true;
    const encodeImgUrl = (url: string) => {
      const domain = url.slice(0, url.indexOf("/", 8) + 1);
      const folder = url.slice(url.indexOf("/", 8) + 1);
      const encode = folder.includes("/") ? encodeURIComponent(folder) : folder;
      return domain + encode;
    };

    try {
      const encodedUrl = encodeImgUrl(this.file.url);

      // Trường hợp photo thì vẫn có thể dùng stream để show % như file thường
      const response = await fetch(encodedUrl, { signal: this.downloadAbortController.signal });
      if (!response.ok || !response.body) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }

      const contentLength = parseInt(response.headers.get("Content-Length") || "0", 10);
      const reader = response.body.getReader();
      let received = 0;
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          chunks.push(value);
          received += value.length;

          if (contentLength > 0) {
            const percent = Math.round((received / contentLength) * 100);
            this.updateSingleFileProgress("downloading", `Downloading ${filename}... (${percent}%)`, percent);
          } else {
            this.updateDownloadProgress(0, 1, `Downloaded ${received} bytes...`);
          }
        }
      }

      const blob = new Blob(chunks as any, { type: response.headers.get("Content-Type") || "image/jpeg" });

      this.updateSingleFileProgress("completed", `Downloaded ${filename}`);
      this.completeDownload("Download completed successfully");
      this.isDownloading = false;
      this.toastr.success(`${filename} downloaded successfully`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "download.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.message === "Download cancelled" || error.name === "AbortError") {
        this.onCancelDownload();
      } else {
        this.completeDownload("Download failed");
        this.toastr.error("Failed to download file");
      }
      this.isDownloading = false;
    }
  }

  // Download progress methods
  private initializeDownloadProgress(): void {
    this.downloadCancelled = false;
    this.downloadProgress = {
      current: 0,
      total: 1,
      percentage: 0,
      currentFile: "Preparing download...",
      status: "downloading",
      message: "",
    };
    this.showDownloadProgress = true;
  }

  private updateSingleFileProgress(
    stage: "preparing" | "downloading" | "completed",
    filename: string,
    percent?: number
  ): void {
    let percentage = 0;
    let current = 0;

    switch (stage) {
      case "preparing":
        percentage = 0;
        current = 0;
        break;
      case "downloading":
        percentage = percent;
        current = 0;
        break;
      case "completed":
        percentage = 100;
        current = 1;
        break;
    }

    // Create a new object to trigger change detection
    this.downloadProgress = {
      ...this.downloadProgress,
      current: current,
      total: 1,
      percentage: percentage,
      currentFile: filename,
      status: "downloading",
    };
  }
  private updateDownloadProgress(current: number, total: number, currentFile: string): void {
    this.downloadProgress = {
      ...this.downloadProgress,
      current: current,
      total: total,
      percentage: Math.round((current / total) * 100),
      currentFile: currentFile,
      status: "downloading",
    };
  }

  private completeDownload(message: string): void {
    this.downloadProgress = {
      ...this.downloadProgress,
      message: message,
      status: message.includes("failed") ? "error" : "completed",
    };

    // Hide progress after 2 seconds
    setTimeout(() => {
      this.showDownloadProgress = false;
    }, 2000);
  }

  onCancelDownload(): void {
    if (this.downloadProgress.status === "completed" || this.downloadProgress.status === "error") {
      this.showDownloadProgress = false;
      this.isDownloading = false;
      this.showDownloadProgress = false;
      this.downloadProgress.status = "idle";
      this.loadingService.complete();
      return;
    }
    this.downloadCancelled = true;

    // Abort all ongoing fetch requests
    if (this.downloadAbortController) {
      this.downloadAbortController.abort();
      this.downloadAbortController = null;
    }

    this.showDownloadProgress = false;
    this.isDownloading = false;
    this.downloadProgress.status = "idle";
    this.toastr.info("Download cancelled", "Info");
    this.loadingService.complete(); // Complete loading service
  }

  // Navigation methods
  get canGoNext(): boolean {
    return this.files.length > 0 && this.currentIndex < this.files.length - 1;
  }

  get canGoPrev(): boolean {
    return this.files.length > 0 && this.currentIndex > 0;
  }

  goToNext(): void {
    if (this.canGoNext && !this.isImageLoading) {
      // Clear any pending navigation
      if (this.navigationTimeout) {
        clearTimeout(this.navigationTimeout);
      }

      // Throttle navigation to prevent rapid clicking
      this.navigationTimeout = setTimeout(() => {
        this.currentIndex++;
        this.loadFile(this.files[this.currentIndex]);
        if (this.isCurrentImage()) {
          this.resetZoom(); // Reset zoom when changing image files
        } else {
          this.zoomLevel = 1;
          this.translateX = 0;
          this.translateY = 0;
        }
        this.preloadAdjacentImages(); // Preload new adjacent images
      }, 50);
    }
  }

  goToPrev(): void {
    if (this.canGoPrev && !this.isImageLoading) {
      // Clear any pending navigation
      if (this.navigationTimeout) {
        clearTimeout(this.navigationTimeout);
      }

      // Throttle navigation to prevent rapid clicking
      this.navigationTimeout = setTimeout(() => {
        this.currentIndex--;
        this.loadFile(this.files[this.currentIndex]);
        if (this.isCurrentImage()) {
          this.resetZoom(); // Reset zoom when changing image files
        } else {
          this.zoomLevel = 1;
          this.translateX = 0;
          this.translateY = 0;
        }
        this.preloadAdjacentImages(); // Preload new adjacent images
      }, 50);
    }
  }

  private loadFile(file: any): void {
    if (!file || !file.url) return;

    const isImage = this.isImageFile(file);

    if (isImage && !shouldRenderPhotoInBrowser(file)) {
      this.isImageLoading = false;
      this.file = { ...file, fileSize: this.getFileSize(file) };
      setTimeout(() => this.applyBrandStyles(), 100);
      return;
    }

    const displayUrl = getBrowserPhotoDisplayUrl(file);
    const cachedImage = displayUrl ? this.imageCache.get(displayUrl) : undefined;

    if (isImage && cachedImage && cachedImage.complete && cachedImage.naturalWidth > 0) {
      // Image is already preloaded, use it immediately
      this.isImageLoading = false;
      this.file = { ...file, fileSize: this.getFileSize(file) };
      // Re-apply brand styles after file change
      setTimeout(() => {
        this.applyBrandStyles();
      }, 100);
    } else {
      // Only image type should block navigation while loading.
      this.isImageLoading = isImage;
      this.file = { ...file, fileSize: this.getFileSize(file) };
      // Re-apply brand styles after file change
      setTimeout(() => {
        this.applyBrandStyles();
      }, 100);
    }
  }

  private getFileSize(file: any): string {
    if (!file.size || Number(file.size) <= 0) {
      return "Unknown size";
    }
    const bytes = Number(file.size);
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  /**
   * Handle image load event
   */
  onImageLoad(): void {
    this.isImageLoading = false;
    const displayUrl = this.getPhotoDisplayUrl();
    if (displayUrl && !this.imageCache.has(displayUrl)) {
      const img = new Image();
      img.src = displayUrl;
      this.imageCache.set(displayUrl, img);
    }
  }

  /**
   * Handle image error event
   */
  onImageError(): void {
    this.isImageLoading = false;
    if (this.file) {
      this.file.imageLoadError = true;
    }
  }

  // Keyboard navigation
  @HostListener("window:keydown", ["$event"])
  handleKeyDown(event: KeyboardEvent): void {
    // Only handle arrow keys if there are multiple files
    if (this.files.length <= 1 || this.isImageLoading) {
      return;
    }

    // Prevent default behavior for arrow keys
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault();
    }

    if (event.key === "ArrowLeft") {
      this.goToPrev();
    } else if (event.key === "ArrowRight") {
      this.goToNext();
    }
  }

  /**
   * Preload adjacent images for smoother navigation
   */
  private preloadAdjacentImages(): void {
    if ((this.type !== "photo" && this.type !== "media") || this.files.length === 0) return;

    // Cancel any pending preloads
    this.preloadQueue.clear();

    const startIndex = Math.max(0, this.currentIndex - this.PRELOAD_RANGE);
    const endIndex = Math.min(this.files.length - 1, this.currentIndex + this.PRELOAD_RANGE);

    // Preload images in background (non-blocking)
    for (let i = startIndex; i <= endIndex; i++) {
      if (i === this.currentIndex) continue; // Skip current image

      const file = this.files[i];
      if (!file || !file.url) continue;
      if (this.type === "media") {
        const mediaType = this.getMediaType(file);
        const fileName = this.getFileNameForDetect(file);
        const urlPath = this.getUrlPathForDetect(file);
        const isImageLike =
          mediaType.startsWith("image") ||
          mediaType === "photo" ||
          /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif)$/.test(fileName || urlPath);
        if (!isImageLike) continue;
      }

      // Skip if already cached and loaded
      const cached = this.imageCache.get(file.url);
      if (cached && cached.complete) continue;

      // Add to preload queue
      this.preloadQueue.add(i);

      // Preload in background
      setTimeout(() => {
        this.preloadImage(file.url, i);
      }, 0);
    }
  }

  /**
   * Preload a single image
   */
  private preloadImage(url: string, index: number): void {
    if (!url || this.imageCache.has(url)) {
      const cached = this.imageCache.get(url);
      if (cached && cached.complete) {
        this.preloadQueue.delete(index);
        return;
      }
    }

    const img = new Image();
    this.imageCache.set(url, img);

    const cleanup = () => {
      this.preloadQueue.delete(index);
    };

    img.addEventListener("load", cleanup, { once: true });
    img.addEventListener("error", cleanup, { once: true });

    // Start loading
    img.src = url;
  }

  /**
   * Clear image cache to free memory
   */
  private clearImageCache(): void {
    // If cache is still reasonable size, keep current strategy
    if (this.imageCache.size <= this.MAX_CACHE_SIZE) {
      // Keep only current and adjacent images in cache
      const keepUrls = new Set<string>();

      if (this.file.url) {
        keepUrls.add(this.file.url);
      }

      const startIndex = Math.max(0, this.currentIndex - this.PRELOAD_RANGE);
      const endIndex = Math.min(this.files.length - 1, this.currentIndex + this.PRELOAD_RANGE);

      for (let i = startIndex; i <= endIndex; i++) {
        const file = this.files[i];
        if (file.url) {
          keepUrls.add(file.url);
        }
      }

      // Remove images not in keep list
      this.imageCache.forEach((img, url) => {
        if (!keepUrls.has(url)) {
          img.src = ""; // Clear src to free memory
          this.imageCache.delete(url);
        }
      });
    } else {
      // Cache is too large, use LRU strategy - keep only most recent
      const keepUrls = new Set<string>();

      // Keep current image
      if (this.file.url) {
        keepUrls.add(this.file.url);
      }

      // Keep adjacent images
      const startIndex = Math.max(0, this.currentIndex - this.PRELOAD_RANGE);
      const endIndex = Math.min(this.files.length - 1, this.currentIndex + this.PRELOAD_RANGE);

      for (let i = startIndex; i <= endIndex; i++) {
        const file = this.files[i];
        if (file.url) {
          keepUrls.add(file.url);
        }
      }

      // Remove all others
      this.imageCache.forEach((img, url) => {
        if (!keepUrls.has(url)) {
          img.src = ""; // Clear src to free memory
          this.imageCache.delete(url);
        }
      });
    }
  }

  ngOnDestroy(): void {
    // Cleanup
    if (this.navigationTimeout) {
      clearTimeout(this.navigationTimeout);
    }

    if (this.cacheCleanupInterval) {
      clearInterval(this.cacheCleanupInterval);
    }

    // Clear image cache
    this.imageCache.forEach(img => {
      img.src = ""; // Clear src to free memory
    });
    this.imageCache.clear();
    this.preloadQueue.clear();
  }
}
